/**
 * Floor Plan Overlay Types
 * Supports importing PDF/DWG floor plans as map overlays with manual alignment
 */

import type { TileMetadata } from './project';

/**
 * Transform parameters for positioning a floor plan on the map
 */
export interface FloorPlanTransform {
  /** Center point longitude (WGS84) */
  anchorLng: number;
  /** Center point latitude (WGS84) */
  anchorLat: number;
  /** Meters per pixel in X direction */
  scaleX: number;
  /** Meters per pixel in Y direction */
  scaleY: number;
  /** Rotation in degrees clockwise from north */
  rotation: number;
}

/**
 * A single floor plan overlay
 */
export interface FloorPlanOverlay {
  /** Unique identifier */
  id: string;
  /** Display name (e.g., "Floor 1", "Basement") */
  name: string;
  /** Floor number for sorting (-1=Basement, 0=Ground, 1+) */
  floorNumber: number;
  /** Original source file path */
  sourcePath: string;
  /** Source file type */
  sourceType: 'pdf' | 'dwg' | 'image';
  /** Page number for multi-page PDFs (1-indexed) */
  sourcePage?: number;
  /** Hash pointing to tile cache directory */
  tileCache?: string;
  /** Tile metadata for rendering */
  tileMetadata?: TileMetadata;
  /** Image dimensions (before tiling) */
  imageWidth?: number;
  imageHeight?: number;
  /** Transform for positioning on map */
  transform: FloorPlanTransform;
  /** Whether this overlay is currently visible */
  visible: boolean;
  /** Opacity (0-1) */
  opacity: number;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
}

/**
 * Collection of floor plan overlays for a project
 */
export interface FloorPlanData {
  overlays: FloorPlanOverlay[];
}

/**
 * Result from floor plan conversion (PDF/image to PNG)
 */
export interface FloorPlanConversionResult {
  success: boolean;
  pngPath?: string;
  width?: number;
  height?: number;
  error?: string;
}

/**
 * Result from floor plan tiling
 */
export interface FloorPlanTilingResult {
  success: boolean;
  cacheHash?: string;
  metadata?: TileMetadata;
  error?: string;
}

/**
 * Progress event for floor plan processing
 */
export interface FloorPlanProgress {
  stage: 'converting' | 'tiling' | 'complete' | 'error';
  progress: number; // 0-100
  message: string;
}

/**
 * Calculate geographic bounds from transform and image dimensions
 */
export function calculateBoundsFromTransform(
  transform: FloorPlanTransform,
  imageWidth: number,
  imageHeight: number
): { west: number; south: number; east: number; north: number } {
  // Calculate half-dimensions in meters
  const halfWidthMeters = (imageWidth * transform.scaleX) / 2;
  const halfHeightMeters = (imageHeight * transform.scaleY) / 2;

  // Convert meters to degrees (approximate at anchor latitude)
  const metersPerDegreeLat = 111320; // Meters per degree latitude (constant)
  const metersPerDegreeLng = 111320 * Math.cos(transform.anchorLat * Math.PI / 180);

  const halfWidthDegrees = halfWidthMeters / metersPerDegreeLng;
  const halfHeightDegrees = halfHeightMeters / metersPerDegreeLat;

  // Note: Rotation is handled during tile generation, not in bounds calculation
  // The bounds are for the axis-aligned bounding box
  return {
    west: transform.anchorLng - halfWidthDegrees,
    south: transform.anchorLat - halfHeightDegrees,
    east: transform.anchorLng + halfWidthDegrees,
    north: transform.anchorLat + halfHeightDegrees,
  };
}

/**
 * Create a default transform centered at given coordinates
 */
export function createDefaultTransform(
  centerLng: number,
  centerLat: number,
  defaultScaleMetersPerPixel = 0.1
): FloorPlanTransform {
  return {
    anchorLng: centerLng,
    anchorLat: centerLat,
    scaleX: defaultScaleMetersPerPixel,
    scaleY: defaultScaleMetersPerPixel,
    rotation: 0,
  };
}

/**
 * Create a new floor plan overlay with default values
 */
export function createFloorPlanOverlay(
  id: string,
  name: string,
  floorNumber: number,
  sourcePath: string,
  sourceType: 'pdf' | 'dwg' | 'image',
  transform: FloorPlanTransform,
  sourcePage?: number
): FloorPlanOverlay {
  const now = new Date().toISOString();
  return {
    id,
    name,
    floorNumber,
    sourcePath,
    sourceType,
    sourcePage,
    transform,
    visible: true,
    opacity: 0.8,
    createdAt: now,
    updatedAt: now,
  };
}
